import { useCallback, useEffect, useState } from 'react';
import { ApiError, Badge, Button, Card, ErrorNote, VehicleForm, api, cx } from '@mobi/web';

interface Vehicle {
  id: number;
  type: 'car' | 'moto';
  plate: string;
  model: string;
  color: string;
  year: number;
  status: 'pending' | 'approved' | 'rejected';
  hasCrlv: boolean;
  active: boolean;
}

const STATUS_TONE = { approved: 'good', pending: 'warn', rejected: 'bad' } as const;
const STATUS_LABEL = { approved: 'aprovado', pending: 'em análise', rejected: 'recusado' } as const;

/**
 * Frota do motorista: qual carro está em uso agora, e trocar entre os
 * que ele já cadastrou. `POST /api/driver/vehicle` faz upsert por placa,
 * então "adicionar" e "editar" acontecem no mesmo formulário — só o
 * segundo é aberto com os dados já preenchidos.
 */
export function Fleet() {
  const [vehicles, setVehicles] = useState<Vehicle[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<{ vehicles: Vehicle[] }>('/api/driver/vehicles');
      setVehicles(res.vehicles);
    } catch {
      setVehicles([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function activate(id: number) {
    setSwitching(id);
    setError(null);
    try {
      await api(`/api/driver/vehicles/${id}/active`, { method: 'POST', body: {} });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'não foi possível trocar');
    } finally {
      setSwitching(null);
    }
  }

  if (vehicles === null) return null;

  if (adding || editing) {
    return (
      <Card className="mt-4 space-y-4">
        <p className="font-semibold">{editing ? `Editar ${editing.plate}` : 'Novo veículo'}</p>
        <VehicleForm
          initial={
            editing
              ? {
                  type: editing.type,
                  plate: editing.plate,
                  model: editing.model,
                  color: editing.color,
                  year: String(editing.year),
                }
              : undefined
          }
          submitLabel={editing ? 'Salvar alterações' : 'Adicionar veículo'}
          onDone={() => {
            setAdding(false);
            setEditing(null);
            void load();
          }}
          onError={setError}
        />
        {error && <ErrorNote>{error}</ErrorNote>}
        <Button variant="ghost" full onClick={() => (setAdding(false), setEditing(null))}>
          Cancelar
        </Button>
      </Card>
    );
  }

  return (
    <Card className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="font-semibold">Seus veículos</p>
        <Button variant="outline"  onClick={() => setAdding(true)}>
          + Adicionar
        </Button>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {vehicles.length === 0 && (
        <p className="text-sm text-muted">Nenhum veículo cadastrado ainda.</p>
      )}

      <div className="space-y-2">
        {vehicles.map((v) => (
          <div
            key={v.id}
            className={cx(
              'flex items-center justify-between gap-3 rounded-2xl border p-3',
              v.active ? 'border-brand bg-brand/5' : 'border-line',
            )}
          >
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setEditing(v)}>
              <p className="truncate font-semibold">
                {v.color} {v.model}
              </p>
              <p className="tabular text-sm text-muted">
                {v.plate} · {v.type === 'moto' ? 'moto' : 'carro'}
              </p>
              <div className="mt-1 flex items-center gap-2">
                <Badge tone={STATUS_TONE[v.status]}>{STATUS_LABEL[v.status]}</Badge>
                {!v.hasCrlv && <Badge tone="warn">sem CRLV</Badge>}
              </div>
            </button>

            {v.active ? (
              <Badge tone="brand">em uso</Badge>
            ) : (
              <Button
                variant="outline"
                
                loading={switching === v.id}
                onClick={() => void activate(v.id)}
              >
                Usar
              </Button>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
